import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Tooltip, Typography } from '@mui/material';
import { fetchNurseCertificationCodes } from '../api/nurseCertificationCodes';
import { NurseCertificationCode } from '../types/nurse';
import { getHumanReadableTimeAgo } from '../utils/dateUtils';

const CertificationCodes: React.FC = () => {
  const [ codes, setCodes ] = useState<NurseCertificationCode[]>([]);
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState<string | null>(null);
  const currentMonth = String(new Date().getMonth() + 1); // 現在の月を取得 (1-12)
  useEffect(() => {
    const loadCertificationCodes = async() => {
      try {
        setLoading(true);
        const data = await fetchNurseCertificationCodes();
        // データの構造を確認して適切に処理
        if (data && Array.isArray(data)) {
          // データが配列の場合
          setCodes(data);
        } else if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
          // ページネーションレスポンスの場合（{ results: [...] }）
          setCodes(data.results);
        } else {
          // 不正なデータ形式の場合
          setError('データの形式が不正です。');
          setCodes([]); // 空の配列を設定
        }
      } catch (err: any) {
        setError('認証コードの取得に失敗しました。');
        setCodes([]); // エラー時は空の配列を設定
      } finally {
        setLoading(false);
      }
    };
    loadCertificationCodes();
  }, []);
  // 月の表示用フォーマット関数
  const formatMonth = (month: string) => {
    return `${month}月`;
  };
  // 作成日時のフォーマット
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${year}年${month}月${day}日 ${hours}:${minutes}`;
    } catch (error) {
      return dateStr;
    }
  };
  // 現在の月と一致するかどうか
  const isCurrentMonth = (month: string) => {
    return month === currentMonth;
  };
  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress/>
          </Box>
        ) : (
          <Box sx={{ px: 3, pt: 2 }}>
            <TableContainer>
              <Table sx={{ '& .MuiTableRow-root': { height: '80px' } }}>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow
                      key={code.id}
                      sx={isCurrentMonth(code.month) ? {
                        bgcolor: '#e3f2fd',
                        '&:hover': { bgcolor: '#bbdefb' },
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          bgcolor: '#1976d2'
                        }
                      } : {}}
                    >
                      <TableCell width="30%" align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {isCurrentMonth(code.month) && (
                            <Box component="span" sx={{
                              mr: 2,
                              px: 1.5,
                              py: 0.7,
                              borderRadius: 1.5,
                              bgcolor: '#1976d2',
                              color: 'white',
                              fontSize: '0.85rem',
                              fontWeight: 'bold',
                            }}>
                              現在
                            </Box>
                          )}
                          <Typography
                            variant="h5"
                            sx={isCurrentMonth(code.month) ? {
                              fontWeight: 'bold',
                              color: '#1976d2',
                              fontSize: '1.5rem'
                            } : {
                              fontSize: '1.4rem'
                            }}
                          >
                            {formatMonth(code.month)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell width="40%" align="center">
                        <Typography
                          variant="h5"
                          sx={isCurrentMonth(code.month) ? {
                            fontWeight: 'bold',
                            fontFamily: 'monospace',
                            letterSpacing: '0.08em',
                            fontSize: '1.8rem'
                          } : {
                            fontFamily: 'monospace',
                            letterSpacing: '0.08em',
                            fontSize: '1.6rem'
                          }}
                        >
                          {code.code}
                        </Typography>
                      </TableCell>
                      <TableCell width="15%" align="left">
                        <Tooltip title={formatDate(code.created_at)} placement="top">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            作成: {getHumanReadableTimeAgo(code.created_at)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell width="15%" align="left">
                        <Tooltip title={formatDate(code.updated_at)} placement="top">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            更新: {getHumanReadableTimeAgo(code.updated_at)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
export default CertificationCodes;
